import { CommandProps, RawCommands } from '@tiptap/core';
import { ImageItem } from './ImageToolbarButton';
import ImageExtension, { type ImageOptions } from '@tiptap/extension-image';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ImageViewBlock } from './ImageViewBlock';
import { isImageSizePreset, type ImageSizePreset } from './imageSizing';

declare module '@tiptap/extension-image' {
  interface ImageOptions {
    imagePicker: (() => Promise<ImageItem[]>) | null;
  }
}

declare module '@tiptap/core' {
  interface Storage {
    image: {
      imagePicker: (() => Promise<ImageItem[]>) | null;
    };
  }
}

const ImagePickerExtension = ImageExtension.extend({
  addOptions() {
    return {
      ...(this.parent?.() ?? ({} as ImageOptions)),
      imagePicker: null,
      /**
       * The old editor wrapped images in empty paragraphs and allows
       * inline images. Ideally, we should only allow block images, but
       * transforming the content to adhere to that would be quite complex.
       * For now, we've decided to continue supporting inline images. */
      inline: true,
    };
  },

  addStorage() {
    return {
      imagePicker: null as (() => Promise<ImageItem[]>) | null,
    };
  },

  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      size: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const size = element.getAttribute('data-size');
          return isImageSizePreset(size) ? size : null;
        },
        renderHTML: (attributes: { size?: ImageSizePreset | null }) =>
          attributes.size ? { 'data-size': attributes.size } : {},
      },
    };
  },

  onCreate() {
    this.storage.imagePicker = this.options.imagePicker;
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageViewBlock);
  },

  addCommands() {
    return {
      insertImages:
        (images: ImageItem[]) =>
        ({ chain }: CommandProps) => {
          images.map(image => {
            chain()
              .focus()
              .insertContentAt(this.editor.state.selection.anchor, {
                type: this.type.name,
                attrs: {
                  id: image.uri,
                  src: image.uri,
                  size: image.sizePreset ?? null,
                },
              })
              .run();
          });
        },
    } as Partial<RawCommands>;
  },
});

export const IMAGE_EXTENSION_NAME = ImagePickerExtension.name;

export { ImagePickerExtension };
